import type React from 'react';
import type { ReplanningStageId } from '~/state/atoms/adaptiveReplanningAtoms';
import type { WorkflowStepId } from '../../WorkflowStepper/workflowStepIds';
import RegisterStage from './RegisterStage';
import RegistrationQaStage from './RegistrationQaStage';
import ReviewStage from './ReviewStage';
import SelectionStage from './SelectionStage';
import SyntheticImagingStage from './SyntheticImagingStage';

export interface ReplanningStage {
    readonly id: ReplanningStageId;
    readonly labelId: string;
    readonly descriptionId: string;
    readonly hostStepId: WorkflowStepId;
    readonly content: React.ComponentType;
    readonly presentation: 'blend' | 'single' | 'none';
    readonly needsPropagatedSet?: boolean;
    readonly showsDose: boolean;
    readonly showsDoseAnalysis: boolean;
    // Each viewport row keeps its own dose, so the two can be compared.
    readonly comparesDoseSideBySide: boolean;
    readonly dimsOtherStructures: boolean;
}

export const REPLANNING_STAGES: readonly ReplanningStage[] = [
    {
        id: 'selection',
        labelId: 'replanning.stage.selection',
        descriptionId: 'replanning.stage.selection.description',
        hostStepId: 'setup',
        content: SelectionStage,
        presentation: 'none',
        showsDose: true,
        showsDoseAnalysis: false,
        comparesDoseSideBySide: false,
        dimsOtherStructures: false,
    },
    {
        id: 'synthetic-imaging',
        labelId: 'replanning.stage.synthetic',
        descriptionId: 'replanning.stage.synthetic.description',
        hostStepId: 'synthetic-imaging',
        content: SyntheticImagingStage,
        presentation: 'none',
        showsDose: false,
        showsDoseAnalysis: false,
        comparesDoseSideBySide: false,
        dimsOtherStructures: false,
    },
    {
        id: 'register',
        labelId: 'replanning.stage.register',
        descriptionId: 'replanning.stage.register.description',
        hostStepId: 'registration',
        content: RegisterStage,
        presentation: 'blend',
        showsDose: false,
        showsDoseAnalysis: false,
        comparesDoseSideBySide: false,
        dimsOtherStructures: false,
    },
    {
        id: 'registration-qa',
        labelId: 'replanning.stage.registrationQa',
        descriptionId: 'replanning.stage.registrationQa.description',
        hostStepId: 'registration',
        content: RegistrationQaStage,
        needsPropagatedSet: true,
        presentation: 'none',
        showsDose: false,
        showsDoseAnalysis: false,
        comparesDoseSideBySide: false,
        dimsOtherStructures: false,
    },
    {
        id: 'review',
        labelId: 'replanning.stage.review',
        descriptionId: 'replanning.stage.review.description',
        hostStepId: 'review',
        content: ReviewStage,
        needsPropagatedSet: true,
        presentation: 'single',
        showsDose: true,
        showsDoseAnalysis: true,
        comparesDoseSideBySide: true,
        dimsOtherStructures: true,
    },
];

export function replanningStage(id: ReplanningStageId): ReplanningStage {
    return (
        REPLANNING_STAGES.find((stage) => stage.id === id) ??
        REPLANNING_STAGES[0]
    );
}
